/** @format */

import { useMemo, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { Auth } from "aws-amplify";
import { ZendeskAPI } from "react-zendesk";
import { Intent } from "@blueprintjs/core";
import { useQuery } from "@apollo/client";
import { useClientProvider } from "providers/clientProvider";
import {
    CLIENT_DISPLAY_QUERY,
    ClientDisplayData,
} from "./graphql/QGetClientData";

import SignUp from "./SignUp";
import {
    showToast,
    // promiseWithTimeout,
    // getResource,
    onError,
} from "app/AppSupport/Utils";
import { Actions } from "redux/app_actions";
import parentLogger from "app/utils/loggerService";

const logger = parentLogger.child({
    module: "SignInSignUpParent",
});

function useQueryParams() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
}

const SignInSignUpParent = () => {
    const dispatch = useDispatch();
    const query = useQueryParams();
    const { state: clientProviderState, dispatch: clientProviderDispatch } =
        useClientProvider();

    let vClientShortName = query.get("client");
    logger.debug(`provided client name is ${vClientShortName}`);
    if (!vClientShortName) {
        vClientShortName = "concertage";
    }
    const { loading, error, data } = useQuery<ClientDisplayData>(
        CLIENT_DISPLAY_QUERY,
        {
            variables: { vClientShortName },
        }
    );

    const signIn = async (emailOrMobile) => {
        try {
            const user = await Auth.signIn(emailOrMobile, undefined);
            dispatch(Actions.saveRawCognitoUser({ rawCognitoUser: user }));
        } catch (e) {
            logger.error("error in sign in");
            logger.error(e);
        }
    };

    // private async fnCall(user, code) {
    //     await promiseWithTimeout(
    //         6000,
    //         () => Auth.sendCustomChallengeAnswer(user, code),
    //         "no response"
    //     );
    // }

    const handleCodeSubmit = async (user, code) => {
        try {
            console.log(
                `sending custom challenge answer for user "${JSON.stringify(
                    user
                )}": ${code}`
            );
            // const userInfo = await Auth.currentUserInfo();
            // console.log(`userInfo is: ${JSON.stringify(userInfo)}`);
            // const fnCall = async () => {
            //     try {
            //         const result = await Auth.sendCustomChallengeAnswer(
            //             user,
            //             code
            //         );
            //         logger.debug(result);
            //     } catch (e) {
            //         onError(null, e, {
            //             location: "fnCall",
            //             authCall: "Auth.sendCustomChallengeAnswer",
            //         });
            //     }
            //     // console.log("in fnCall");
            //     let userInfo = null;
            //     // const userInfo = await Auth.currentUserInfo();
            //     // console.log(`have userInfo: "${JSON.stringify(userInfo)}"`);
            //     if (userInfo === null) {
            //         const result = await promiseWithTimeout(
            //             5000,
            //             () => Auth.sendCustomChallengeAnswer(user, code),

            //             "no response"
            //         );
            //         // console.log(`returning result: ${JSON.stringify(result)}`);
            //         return result;
            //     } else {
            //         console.log("already logged in");
            //         return "already logged in";
            //     }
            // };

            // trying without retry
            try {
                const result = await Auth.sendCustomChallengeAnswer(user, code);
                logger.debug(result);
            } catch (e) {
                onError(null, e, {
                    location: "fnCall",
                    authCall: "Auth.sendCustomChallengeAnswer",
                });
            }
            // const userResponse =
            // await getResource({ code, user }, fnCall, 2);
            // console.log("userResponse to follow");
            // console.log(userResponse);
            // N.B. there should be an auth event if this is successful, but sometimes there is a timeout
            // TODO: find out what happens if the code is incorrect
        } catch (err: any) {
            // TODO: handle this error for the user
            onError(`Error: ${err.name}: ${err.message}`, err, {
                code,
                ...user,
            });
        }
    };

    const stableInitializer = useCallback(() => {
        logger.debug("inside stableInitializer");
        dispatch(Actions.getIpData({})); // only done once when app loads
        ZendeskAPI("webWidget", "show");
    }, [dispatch]);

    useEffect(() => {
        logger.debug(
            "inside a useEffect that should occur only when first rendered"
        );
        stableInitializer();
    }, [stableInitializer]); //now shut up eslint

    useEffect(() => {
        logger.debug("in useEffect to save client info to client provider");
        if (data?.clientDisplayData && vClientShortName) {
            clientProviderDispatch({
                type: "saveClient",
                value: {
                    clientId: data.clientDisplayData.clientId,
                    clientName: data.clientDisplayData.clientName,
                    clientShortName: vClientShortName,
                    clientType: data.clientDisplayData.isIndividualClient
                        ? "individual"
                        : "entity",
                    titleText: data.clientDisplayData.titleText,
                    subtitleText: data.clientDisplayData.subtitleText,
                    logoS3Filename: data.clientDisplayData.s3Filename,
                    additionalInfoMd: data.clientDisplayData.additionalInfoMd,
                    colorScheme: data.clientDisplayData.colorScheme,
                },
            });
        }
    }, [data, dispatch, vClientShortName, clientProviderDispatch]);

    if (loading || clientProviderState.clientData.clientShortName === "none") {
        return <div>Loading initial data</div>;
    }

    if (error) {
        logger.error("error getting client display data");
        logger.error(error);
    }

    return (
        <>
            <SignUp
                // signUpFn={this.signUp}
                signUpFn={() =>
                    showToast(
                        "Sign up is not yet provided.  Please get in touch at info@concertage.com with a request to be added",
                        10000,
                        Intent.WARNING
                    )
                }
                signInFn={signIn}
                handleCodeSubmit={handleCodeSubmit}
                clientDisplayData={clientProviderState.clientData}
            />
        </>
    );
};

export default SignInSignUpParent;

/* <GoogleReCaptchaProvider reCaptchaKey="6Ld5GOkUAAAAAK6G4uySLmTxXPPUlC4yGtBK0296"> */
/* <GoogleReCaptcha
                        onVerify={(token) => this.setRecaptchaToken(token)}
                    /> */
/* </GoogleReCaptchaProvider> */
// import {
//     GoogleReCaptchaProvider,
//     GoogleReCaptcha,
// } from "react-google-recaptcha-v3";
// private signUp = async (formData, language) => {
//     console.log("signing up");
//     await Auth.currentCredentials();
//     console.log("finished with Auth.currentCredentials()");
//     const { geoCity, geoCountry } = this.props;
//     console.log(
//         `have geoCity and geoCountry of ${geoCity} and ${geoCountry}`
//     );
//     const { recaptchaToken } = this.state;
//     console.log(`have recaptca token of ${recaptchaToken}`);
//     const payload = {
//         country: geoCountry,
//         city: geoCity,
//         recaptchaToken,
//         host: window.location.host,
//         ...formData,
//         language,
//     };
//     console.log(`have signup payload of ${JSON.stringify(payload)}`);
//     this.props.signUp(payload);
// };

// private setRecaptchaToken = (token) => {
//     this.setState({
//         recaptchaToken: token,
//     });
// };
